import { useEffect, useState } from 'react';
import { Braces, Check, Copy, X } from 'lucide-react';
import { toast } from 'sonner';

type SubscriberJsonModalProps = {
  isOpen: boolean;
  onClose: () => void;
  jsonString: string;
};

export default function SubscriberJsonModal({
  isOpen,
  onClose,
  jsonString,
}: SubscriberJsonModalProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setCopied(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  async function handleCopy() {
    if (!jsonString) return;
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      toast.success('JSON copied to clipboard');
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed');
    }
  }

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-3xl flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscriber-json-modal-title"
      >
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 min-w-0">
            <Braces className="w-5 h-5 shrink-0 text-gray-500 dark:text-gray-400" />
            <h2
              id="subscriber-json-modal-title"
              className="text-lg font-semibold text-gray-900 dark:text-white"
            >
              Raw JSON
            </h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => void handleCopy()}
              disabled={!jsonString}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1 -m-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <pre className="p-4 text-xs font-mono text-gray-800 dark:text-gray-100 overflow-auto flex-1 min-h-0 bg-gray-50 dark:bg-gray-900/40">
          {jsonString || '—'}
        </pre>
      </div>
    </div>
  );
}
